import React from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { EllipsisVertical } from 'lucide-react'

export interface MoreButtonItem {
  name: string
  onClick: () => void
  disabled?: boolean
  'data-test'?: string
}

interface IMoreButtonProps extends React.ComponentPropsWithoutRef<'button'> {
  items: MoreButtonItem[]
  position?: Placement
}

type Placement =
  | 'topLeft'
  | 'topCenter'
  | 'topRight'
  | 'bottomLeft'
  | 'bottomCenter'
  | 'bottomRight'

const placementMap: Record<
  Placement,
  { side: 'top' | 'bottom'; align: 'start' | 'center' | 'end' }
> = {
  topLeft: { side: 'top', align: 'start' },
  topCenter: { side: 'top', align: 'center' },
  topRight: { side: 'top', align: 'end' },
  bottomLeft: { side: 'bottom', align: 'start' },
  bottomCenter: { side: 'bottom', align: 'center' },
  bottomRight: { side: 'bottom', align: 'end' },
}

const MoreButton: React.FC<IMoreButtonProps> = ({
  items,
  position = 'bottomRight',
  disabled = false,
  onClick,
  onMouseDown,
  onPointerDown,
  ...buttonProps
}) => {
  const { side, align } = placementMap[position]

  const handleTriggerClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onClick?.(event)
  }

  const handleTriggerMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onMouseDown?.(event)
  }

  const handleTriggerPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onPointerDown?.(event)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        onClick={handleTriggerClick}
        onMouseDown={handleTriggerMouseDown}
        onPointerDown={handleTriggerPointerDown}
        render={
          <Button
            variant='transparent'
            size='icon-xl'
            className='w-fit h-10'
            type='button'
            disabled={disabled}
            {...buttonProps}
          />
        }
      >
        <EllipsisVertical className='size-5.5' />
      </DropdownMenuTrigger>
      <DropdownMenuContent side={side} align={align}>
        {items.map((item, index) => (
          <DropdownMenuItem
            key={`${item.name}-${index}`}
            disabled={item.disabled}
            onClick={(event) => {
              event.stopPropagation()
              item.onClick()
            }}
            data-test={item['data-test']}
          >
            {item.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default MoreButton
